import { TEXT_COLOR_PALETTE, type TextColorName } from '../constants';
import type { Option } from '../types';

const TEXT_COLOR_OPTIONS: ReadonlyArray<{ key: 'default' | TextColorName; title: string; value: string | null }> = [
  { key: 'default', title: 'Default', value: null },
  { key: 'red', title: 'Warning', value: TEXT_COLOR_PALETTE.red },
  { key: 'green', title: 'Confirmation', value: TEXT_COLOR_PALETTE.green },
  { key: 'blue', title: 'Information', value: TEXT_COLOR_PALETTE.blue },
];

const readAttribute = (editor: any, typeName: string, attrName: string): string | null => {
  const value = editor?.getAttributes?.(typeName)?.[attrName];
  return typeof value === 'string' && value.length > 0 ? value.toLowerCase() : null;
};

// Reads the active colour from the textStyle mark first (the canonical
// inline-text source), then falls back to the listItem's markerColor
// attribute. The fallback is a defensive net for cases where textStyle
// isn't reported as active but the surrounding list item is uniformly
// coloured (e.g. an empty list item that took the marker colour from a
// stored mark), so the toolbar swatch + indicator bar still light up.
// Note: under the current Color extension rules a listItem's
// markerColor is only set when the colour gesture covers the item's
// entire own line (its first paragraph), so the textStyle path will
// normally already light up when the cursor sits in that line.
const getActiveColor = (editor: any): string | null => {
  return readAttribute(editor, 'textStyle', 'color') ?? readAttribute(editor, 'listItem', 'markerColor');
};

export const getActiveTextColor = getActiveColor;

const Swatch = ({ color }: { color: string | null }) => {
  const glyphColor = color ?? 'var(--color-info-900)';
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-6 w-6 items-center justify-center rounded-md border-[1.75px] text-sm font-medium"
      style={{ color: glyphColor, borderColor: glyphColor }}
    >
      A
    </span>
  );
};

export const getTextColorCtrlOptions = (editor: any, onSelected: () => void): Option[] => {
  const activeColor = getActiveColor(editor);

  return TEXT_COLOR_OPTIONS.map(({ key, title, value }) => ({
    format: () => {
      if (value === null) {
        editor?.chain().focus().unsetColor().run();
      } else {
        editor?.chain().focus().setColor(value).run();
      }
      return onSelected();
    },
    isActive: value === null ? activeColor === null : activeColor === value.toLowerCase(),
    key,
    icon: <Swatch color={value} />,
    title,
  }));
};
